import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  FaBold, FaImage, FaItalic, FaLink, FaParagraph, FaQuoteRight,
  FaUnderline, FaVideo,
} from "react-icons/fa";
import { GoListOrdered } from "react-icons/go";
import { RiListUnordered } from "react-icons/ri";
import swal from "sweetalert";
import { BASE_URL_API_BLOG as BASE_URL } from "../Utils/Constant";
import {
  basicauthblog,
  imageUploadApiKey,
  imageUploadBasicAuth,
} from "../Utils/Credentials";
import "../../assets/styles/components/_bumppy-edit-blog.scss";

const formatApiDate = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const formatDateTimeLocal = (date) => {
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// `datetime-local` has no timezone. Build the date from its individual parts so
// browsers consistently interpret the selected value in the user's local time.
const parseDateTimeLocal = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return null;

  const [, year, month, day, hours, minutes] = match.map(Number);
  const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
  const isValid = date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day
    && date.getHours() === hours
    && date.getMinutes() === minutes;

  return isValid ? date : null;
};

const generateSlug = (title) => title.toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const BumppyScheduler = () => {
  const history = useHistory();
  const editorRef = useRef(null);
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [category, setCategory] = useState("");
  const [author, setAuthor] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageDetails, setImageDetails] = useState({
    title: "",
    alt: "",
    description: "",
  });

  const getHeaders = () => {
    const { username, password } = basicauthblog;
    return {
      Authorization: `Basic ${btoa(`${username}:${password}`)}`,
      "Content-Type": "application/json",
    };
  };

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [categoryResponse, authorResponse] = await Promise.all([
          fetch(`${BASE_URL}/GetBlogCategories`, {
            method: "POST", headers: getHeaders(), body: JSON.stringify({ type: "0" }),
          }),
          fetch(`${BASE_URL}/GetBumppyAuthors`, {
            method: "POST", headers: getHeaders(), body: JSON.stringify({}),
          }),
        ]);
        const [categoryResult, authorResult] = await Promise.all([
          categoryResponse.json(), authorResponse.json(),
        ]);
        if (String(categoryResult.status) === "0") {
          setCategories(Array.isArray(categoryResult.data) ? categoryResult.data : []);
        }
        if (String(authorResult.status) === "0") {
          setAuthors(Array.isArray(authorResult.data) ? authorResult.data : []);
        }
      } catch (error) {
        swal("Unable to load options", error.message, "error");
      } finally {
        setIsLoadingOptions(false);
      }
    };
    loadOptions();
  }, []);

  const formatText = (command, value = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(command, false, value);
  };
  const insertLink = () => {
    const url = window.prompt("Enter the URL");
    if (url) formatText("createLink", url);
  };
  const insertImage = () => {
    const url = window.prompt("Enter the image URL");
    if (url) formatText("insertImage", url);
  };
  const insertVideo = () => {
    const input = window.prompt("Enter a YouTube URL or video ID");
    if (!input) return;
    let id = input.trim();
    try {
      const url = new URL(input);
      id = url.hostname === "youtu.be" ? url.pathname.slice(1) : url.searchParams.get("v") || url.pathname.split("/").pop();
    } catch (error) {
      // Plain YouTube IDs do not need URL parsing.
    }
    id = id.replace(/[^a-zA-Z0-9_-]/g, "");
    formatText("insertHTML", `<iframe src="https://www.youtube.com/embed/${id}" width="560" height="315" frameborder="0" allowfullscreen></iframe>`);
  };

  const handleTitleChange = (event) => {
    const nextTitle = event.target.value;
    setTitle(nextTitle);
    if (!slugEdited) setSlug(generateSlug(nextTitle));
  };

  const uploadFeatureImage = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!imageUploadApiKey) {
      swal("Upload configuration missing", "Set REACT_APP_IMAGE_UPLOAD_API_KEY before uploading images.", "error");
      return;
    }

    const uploadCredentials = btoa(
      `${imageUploadBasicAuth.username}:${imageUploadBasicAuth.password}`
    );
    const formData = new FormData();
    formData.append("image", file);
    setIsUploadingImage(true);
    try {
      const response = await fetch("https://img.bumppy.com/upload_image.php", {
        method: "POST",
        headers: {
          "X-API-Key": imageUploadApiKey,
          Authorization: `Basic ${uploadCredentials}`,
        },
        body: formData,
      });
      const result = await response.json();
      if (!response.ok || !result.status || !result.file_url) {
        throw new Error(result.message || "Image upload failed.");
      }
      setThumbnail(result.file_url);
      swal("Uploaded", result.message || "Feature image uploaded successfully.", "success");
    } catch (error) {
      swal("Upload failed", error.message || "Unable to upload the image.", "error");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const saveFeatureImage = (event) => {
    event.preventDefault();
    if (!thumbnail) {
      swal("Image required", "Upload an image before saving.", "warning");
      return;
    }
    setShowImageModal(false);
  };

  const scheduleBlog = async (event) => {
    event.preventDefault();
    const content = editorRef.current ? editorRef.current.innerHTML.trim() : "";
    if (!title.trim() || !slug.trim() || !excerpt.trim() || !content || !thumbnail.trim() || !category || !author || !scheduledAt) {
      swal("Missing information", "Complete every field before scheduling.", "warning");
      return;
    }

    const scheduleDate = parseDateTimeLocal(scheduledAt);
    if (!scheduleDate) {
      swal("Invalid schedule", "Choose a valid date and time.", "warning");
      return;
    }

    if (scheduleDate.getTime() <= Date.now()) {
      swal(
        "Invalid schedule",
        `The selected time (${scheduleDate.toLocaleString()}) has already passed. Choose a future date and time.`,
        "warning"
      );
      return;
    }

    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const formattedDate = formatApiDate(scheduleDate);
    const payload = {
      post_author: String(author), post_date: formattedDate, post_date_gmt: formattedDate,
      post_content: content, post_title: title.trim(), post_excerpt: excerpt.trim(),
      post_status: "future", comment_status: "open", ping_status: "open",
      post_password: "", post_name: slug.trim(), to_ping: "",
      pinged: `https://www.bumppy.com/${slug.trim()}`, post_modified: formattedDate,
      post_modified_gmt: formattedDate, post_content_filtered: "", post_parent: "0",
      guid: "", menu_order: "0", post_type: "post", post_mime_type: "",
      comment_count: "0", user: String(storedUser.user_id || storedUser.id || ""),
      thumbnail: thumbnail.trim(), category, thumbnail_id: "0", view: "0",
    };

    setIsSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/InsertBumppyBlogs`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || String(result.status) !== "0") {
        throw new Error(result.msg || "Unable to schedule this blog.");
      }
      await swal("Scheduled", result.result?.[0]?.message || result.msg || "Blog scheduled successfully.", "success");
      history.push("/blog/BumppyBlogs");
    } catch (error) {
      swal("Scheduling failed", error.message || "Something went wrong.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <main className="bumppy-edit-page bumppy-scheduler-page">
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Feature image</Modal.Title>
        </Modal.Header>
        <Form onSubmit={saveFeatureImage}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Upload image</Form.Label>
              <Form.Control type="file" accept="image/*" onChange={uploadFeatureImage} />
            </Form.Group>
            {isUploadingImage && <div className="image-uploading"><Spinner size="sm" animation="border" /> Uploading image...</div>}
            {thumbnail && (
              <div className="feature-image-preview">
                <img src={thumbnail} alt={imageDetails.alt || "Feature preview"} />
                <span>{thumbnail}</span>
              </div>
            )}
            <Form.Group>
              <Form.Label>Image title</Form.Label>
              <Form.Control name="title" value={imageDetails.title} onChange={(e) => setImageDetails({ ...imageDetails, title: e.target.value })} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Alt text</Form.Label>
              <Form.Control name="alt" value={imageDetails.alt} onChange={(e) => setImageDetails({ ...imageDetails, alt: e.target.value })} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows="3" name="description" value={imageDetails.description} onChange={(e) => setImageDetails({ ...imageDetails, description: e.target.value })} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowImageModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={!thumbnail || isUploadingImage}>Use feature image</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <header className="edit-page-header">
        <div><span>Bumppy publishing</span><h2>Schedule a blog</h2></div>
        <div className="header-actions">
          <Link to="/blog/BumppyBlogs" className="cancel-button">Cancel</Link>
          <button type="submit" form="bumppy-scheduler-form" disabled={isSaving || isLoadingOptions}>
            {isSaving ? <><Spinner size="sm" animation="border" /> Scheduling...</> : "Schedule blog"}
          </button>
        </div>
      </header>

      <form id="bumppy-scheduler-form" onSubmit={scheduleBlog} className="bumppy-edit-form">
        <section className="edit-main-card">
          <label>Title<input value={title} onChange={handleTitleChange} placeholder="Enter blog title" /></label>
          <div className="two-column-fields">
            <label>Slug<input value={slug} onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }} placeholder="blog-url-slug" /></label>
            <label>Schedule date and time<input type="datetime-local" min={formatDateTimeLocal(new Date(Date.now() + 60000))} value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} /></label>
          </div>
          <div className="two-column-fields">
            <label>Category<select value={category} onChange={(e) => setCategory(e.target.value)}><option value="">Select category</option>{categories.map((item) => <option key={item.id} value={item.category_name}>{item.category_name}</option>)}</select></label>
            <label>Author<select value={author} onChange={(e) => setAuthor(e.target.value)}><option value="">Select author</option>{authors.map((item) => <option key={item.id} value={item.id}>{item.display_name} ({item.username})</option>)}</select></label>
          </div>
          <label>Excerpt<textarea rows="3" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="Short blog summary" /></label>
          <label className="content-label">Content</label>
          <div className="bumppy-editor-toolbar">
            {[1,2,3,4,5,6].map((level) => <button type="button" key={level} onClick={() => formatText("formatBlock", `<h${level}>`)}>H{level}</button>)}
            <button type="button" onClick={() => formatText("formatBlock", "<blockquote>")}><FaQuoteRight /></button>
            <button type="button" onClick={() => formatText("formatBlock", "<p>")}><FaParagraph /></button>
            <button type="button" onClick={() => formatText("insertUnorderedList")}><RiListUnordered /></button>
            <button type="button" onClick={() => formatText("insertOrderedList")}><GoListOrdered /></button>
            <button type="button" onClick={() => formatText("bold")}><FaBold /></button>
            <button type="button" onClick={() => formatText("italic")}><FaItalic /></button>
            <button type="button" onClick={() => formatText("underline")}><FaUnderline /></button>
            <input type="color" aria-label="Text color" onChange={(e) => formatText("foreColor", e.target.value)} />
            <button type="button" onClick={insertLink}><FaLink /></button>
            <button type="button" onClick={insertImage}><FaImage /></button>
            <button type="button" onClick={insertVideo}><FaVideo /></button>
          </div>
          <div className="bumppy-content-editor" ref={editorRef} contentEditable suppressContentEditableWarning />
        </section>
        <aside className="edit-side-card">
          <label>Thumbnail URL<input value={thumbnail} onChange={(e) => setThumbnail(e.target.value)} placeholder="https://img.bumppy.com/..." /></label>
          <Button className="choose-feature-button" variant="info" size="sm" onClick={() => setShowImageModal(true)}>Choose File</Button>
          {thumbnail && <img src={thumbnail} alt={imageDetails.alt || "Scheduled blog thumbnail preview"} />}
          {thumbnail && (
            <div className="feature-image-meta">
              <strong>{imageDetails.title || "No image title"}</strong>
              <span>Alt: {imageDetails.alt || "Not provided"}</span>
              <span>{imageDetails.description || "No description"}</span>
            </div>
          )}
          <div className="slug-preview"><span>Scheduled public URL</span><a href={`https://bumppy.com/${slug}`} target="_blank" rel="noopener noreferrer">bumppy.com/{slug}</a></div>
        </aside>
      </form>
    </main>
  );
};

export default BumppyScheduler;
